import type { Attribute } from 'stores/PaginationTypes';
import AppConfig from 'util/AppConfig';
import DataLakeRoutes from 'data-lake/Routes';
import { SEARCH_LINK_TITLE } from 'components/navigation/bindings';
import {
  INTERNAL_DATA_LAKE_ID,
  INTERNAL_DATA_LAKE_STORAGE_TYPE,
} from 'data-lake/preview/storage-types/internal/Constants';

import EnterpriseRoutes from '../EnterpriseRoutes';

export const DATA_LAKE_API_ROUTES = {
  DataLakeAPIController: {
    paginated: () => ({ url: 'plugins/org.graylog.plugins.datalake/data_lake' }),
    paginatedStreams: () => ({ url: '/plugins/org.graylog.plugins.datalake/data_lake/stream' }),
    get: (id: string) => ({ url: `plugins/org.graylog.plugins.datalake/data_lake/archives/${id}` }),
    get_stream_data_lake: (streamId: string) => ({
      url: `plugins/org.graylog.plugins.datalake/data_lake/stream/${streamId}/archive`,
    }),
    delete: (streamId: string, retrievalOperationId: string) => ({
      url: `/plugins/org.graylog.plugins.datalake/data_lake/archive/${streamId}/retrieved/${retrievalOperationId}`,
    }),
    bulk_delete: () => ({ url: 'plugins/org.graylog.plugins.datalake/data_lake/jobs/delete' }),
    bulk_retrieval: () => ({ url: 'plugins/org.graylog.plugins.datalake/data_lake/jobs/internal/retrieve' }),
    config: {
      base: () => ({ url: 'plugins/org.graylog.plugins.datalake/data_lake/config' }),
    },
    backend: {
      base: () => ({ url: 'plugins/org.graylog.plugins.datalake/data_lake/backends' }),
      get: (id: string) => ({ url: `plugins/org.graylog.plugins.datalake/data_lake/backends/${id}` }),
      delete: (id: string) => ({ url: `plugins/org.graylog.plugins.datalake/data_lake/backends/${id}` }),
      update: (id: string) => ({ url: `plugins/org.graylog.plugins.datalake/data_lake/backends/${id}` }),
    },
    backendConfig: {
      base: () => ({ url: 'plugins/org.graylog.plugins.datalake/data_lake/config' }),
    },
    estimate: () => ({ url: 'plugins/org.graylog.plugins.datalake/data_lake/size_estimate' }),
    job: {
      progress: () => ({ url: 'plugins/org.graylog.plugins.datalake/data_lake/jobs/progress' }),
      stream_jobs: (streamId: string) => ({
        url: `plugins/org.graylog.plugins.datalake/data_lake/jobs/progress/${streamId}`,
      }),
      cancel: (id: string) => ({ url: `plugins/org.graylog.plugins.datalake/data_lake/jobs/${id}` }),
    },
    stream_data_lake_config: (streamId: string) => ({
      url: `plugins/org.graylog.plugins.datalake/data_lake/stream/${streamId}/config`,
    }),
    stream_data_lake_status: (streamId: string) => ({
      url: `plugins/org.graylog.plugins.datalake/data_lake/stream/${streamId}/status`,
    }),
    stream_data_lake_set_config_status: () => ({
      url: 'plugins/org.graylog.plugins.datalake/data_lake/stream/config/enable',
    }),
    stream_retention: (streamId: string) => ({
      url: `plugins/org.graylog.plugins.datalake/data_lake/stream/${streamId}/retention_time`,
    }),
  },
};
export const DATA_LAKE_CONFIG_QUERY_KEYS = ['data-lake', 'config'];
export const DATA_LAKE_BACKEND_QUERY_KEYS = ['data-lake', 'backend'];
export const DATA_LAKE_BACKEND_CONFIG_QUERY_KEYS = ['data-lake', 'backend', 'config'];

type DataLakeSettings = {
  data_lake_enabled: boolean;
  internal_data_lake_enabled: boolean;
};
const settings: DataLakeSettings = AppConfig.pluginUISettings('org.graylog.plugins.storage.data_lake');

export const DATALAKE_ATTRIBUTE_NAME = 'data_lake';
export const DATALAKE_ATTRIBUTES: Array<Attribute> = [{ id: DATALAKE_ATTRIBUTE_NAME, title: 'Data Lake' }];

export const DATA_LAKE_PREVIEW_ENABLED = AppConfig.isFeatureEnabled('data_lake_search');
export const DATA_LAKE_EXTERNAL_SEARCH_ENABLED = AppConfig.isFeatureEnabled('external_data_lake_search');
export const DATA_LAKE_ENABLED = settings?.data_lake_enabled === true;
export const DATA_LAKE_INTERNAL_ENABLED = !AppConfig.isCloud() && settings?.internal_data_lake_enabled === true;

export const DATA_LAKE_LICENSE_TITLE = 'Data Lake is partially Disabled.';
export const DATA_LAKE_LICENSE_TEXT = `Data Lake is partially disabled because there is no valid license. While in this state:\n
   - Streams already configured to use Data Lake will continue to store messages there.\n
   - Streams not already configured to use Data Lake can not be configured to use it.\n
   - Log Retrieval from the Data Lake is disabled.\n
   - Preview of logs from the Data Lake is disabled.\n`;
export const DATA_LAKE_INCLUSION_TYPE = 'DATA_LAKE_ONLY';
export const INDEXER_INCLUSION_TYPE = 'INDEXER_ONLY';
export const BOTH_INCLUSION_TYPE = 'BOTH';
export const HIDE_LAKE_RETRIEVAL_HELP_TEXT_KEY = 'data-lake-retrieval-hide-help-text';
export const HIDE_LAKE_ADD_TO_INVESTIGATION_HELP_TEXT_KEY = 'data-lake-add-to-investigation-hide-help-text';
export const DATALAKE_SECTION_OPENED_KEY = 'data-lake-section-opened';

export const INTERNAL_DATA_LAKE_TYPE = 'INTERNAL' as const;
export const EXTERNAL_DATA_LAKE_TYPE = 'EXTERNAL' as const;

export const DATA_LAKE_PREVIEW_NAV_ITEM = {
  description: 'Data Lake',
  position: { after: SEARCH_LINK_TITLE },
  children: [
    ...(DATA_LAKE_PREVIEW_ENABLED ? [{ path: DataLakeRoutes.PREVIEW, description: 'Preview' }] : []),
    ...(DATA_LAKE_EXTERNAL_SEARCH_ENABLED
      ? [
          {
            path: DataLakeRoutes.RETRIEVALS,
            description: 'Retrievals',
            permissions: 'data_lake:read',
          },
        ]
      : []),
    ...(DATA_LAKE_INTERNAL_ENABLED
      ? [
          {
            path: EnterpriseRoutes.DATA_LAKE,
            description: 'Internal Lake Setup',
            permissions: 'data_lake:read',
          },
        ]
      : []),
    ...(DATA_LAKE_EXTERNAL_SEARCH_ENABLED
      ? [
          {
            path: DataLakeRoutes.CONNECTORS,
            description: 'External Lake Connectors',
            permissions: ['data_lake:read', 'data_lake_backend_config:read'],
          },
        ]
      : []),
  ],
};

export const INTERNAL_DATA_LAKE_INFO = {
  id: INTERNAL_DATA_LAKE_ID,
  type: INTERNAL_DATA_LAKE_TYPE,
  storageType: INTERNAL_DATA_LAKE_STORAGE_TYPE,
  title: 'Data Lake',
  active: true,
} as const;
